import * as createUser from 'store/actions/users/createUsers';
import * as fetchUser from 'store/actions/users/fetchUsers';
import * as updateUser from 'store/actions/users/updateUsers';
import * as deleteUser from 'store/actions/users/deleteUsers';
import * as types from 'store/actions/types';
import apiClient from 'clients/apiClient';
import config from 'helpers/config';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { showNotification } from 'store/actions/uiActions';
import utils from 'helpers/utils';

const getUserStore = (state) => {
    return state.users;
};

function* fetchUsersAccumulatedAsync({ payload }) {
    const params = payload.params || {
        size: payload.size,
        offset: payload.offset,
        sort_by: payload.sort.field,
        sort_direction: payload.sort.order,
    };

    try {
        const url = `${ config.getApiBaseUrl() }/users`;

        const response = yield apiClient.read(url, params);

        yield put(fetchUser.fetchUsersSuccess(response));
    } catch (error) {
        yield put(fetchUser.fetchUsersFailure(error));
    }
}

function* fetchUsersAsync({ payload }) {
    try {
        const url = `${ config.getApiBaseUrl() }/users`;

        const response = yield apiClient.read(url, payload);

        yield put(fetchUser.fetchUsersSuccess(response));
    } catch (error) {
        yield put(fetchUser.fetchUsersFailure(error));
    }
}

function* fetchUserAsync({ payload }) {
    const {
        id,
        onError,
        onSuccess,
    } = payload;

    try {
        const url = `${ config.getApiBaseUrl() }/users/${ id }`;

        const response = yield apiClient.read(url);

        yield put(fetchUser.fetchUserSuccess(response));

        if (utils.isFunction(onSuccess)) {
            yield onSuccess();
        }
    } catch (error) {
        yield put(fetchUser.fetchUserFailure(error));

        if (utils.isFunction(onError)) {
            yield onError(error);
        }
    }
}

function* createUserAsync({ payload }) {
    const {
        onError,
        onSuccess,
        values,
    } = payload;

    try {
        const url = `${ config.getApiBaseUrl() }/users`;
        const response = yield apiClient.create(url, values);

        yield put(createUser.createUserSuccess(response));

        if (utils.isFunction(onSuccess)) {
            yield onSuccess(response);
        }
    } catch (error) {
        yield put(createUser.createUserFailure(error));

        if (utils.isFunction(onError)) {
            yield onError(error);
        }
    }
}

function* createUserSuccessAsync() {
    yield put(showNotification('notifications.users.create.success'));
}

function* createUserConfirmationSuccessAsync() {
    yield put(showNotification('notifications.users.confirmed.success', 'success'));
}

function* updateUserAsync({ payload }) {
    const {
        id,
        onError,
        onSuccess,
        values,
    } = payload;

    try {
        const url = `${ config.getApiBaseUrl() }/users/${ id }`;
        const response = yield apiClient.patch(url, values);

        yield put(updateUser.updateUserSuccess(id, response));

        if (utils.isFunction(onSuccess)) {
            yield onSuccess(response);
        }
    } catch (error) {
        yield put(updateUser.updateUserFailure(error));

        if (utils.isFunction(onError)) {
            yield onError(error);
        }
    }
}

function* updateUserSuccessAsync() {
    yield put(showNotification('notifications.users.update.success'));
}

function* updateUserActivityErrorAsync({ error }) {
    const { message } = error.response;

    yield put(showNotification(message, 'warning'));
}

function* deleteUserAsync({ payload }) {
    const { callback, id } = payload;

    try {
        const url = `${ config.getApiBaseUrl() }/users/${ id }`;

        const response = yield apiClient.delete(url);

        if (utils.isFunction(callback)) {
            yield callback(response.json(), payload);
        }

        yield put(deleteUser.deleteUserSuccess(payload));
    } catch (error) {
        yield put(deleteUser.deleteUserFailure(error));
    }
}

function* deleteUserSuccessAsync() {
    const getUser = yield select(getUserStore);

    yield put(fetchUser.fetchUsers({
        offset: getUser.offset,
        page: getUser.page,
    }));

    yield put(showNotification('notifications.users.delete.success', 'success'));
}

function* deleteUserMembershipAsync({ payload }) {
    const { id, onSuccess, ...params } = payload;

    try {
        const url = `${ config.getApiBaseUrl() }/user-memberships/${ id }`;

        yield apiClient.delete(url, params);

        yield put(deleteUser.deleteUserMembershipSuccess());

        if (utils.isFunction(onSuccess)) {
            yield onSuccess();
        }

        yield put(showNotification('notifications.users.delete.success', 'success'));
    } catch (error) {
        yield put(deleteUser.deleteUserMembershipFailure(error));
    }
}

function* failureMessage({ error }) {
    const { message } = error.response;

    if (!message) {
        return;
    }

    yield put(showNotification(message, 'warning'));
}

function* deleteUserFailureAsync(action) {
    const { code, message } = action.error.response;

    if (code === 400) {
        yield put(showNotification(`resources.users.delete.warnings.${ message }`, 'warning'));
    } else {
        yield call(failureMessage, action);
    }
}

export default function* usersSagas() {
    yield [
        takeEvery(types.FETCH_USERS, fetchUsersAsync),
        takeEvery(types.FETCH_USERS_ACCUMULATED, fetchUsersAccumulatedAsync),
        takeEvery(types.FETCH_USERS_FAILURE, failureMessage),

        takeEvery(types.FETCH_USER, fetchUserAsync),
        takeEvery(types.FETCH_USER_FAILURE, failureMessage),

        takeEvery(types.CREATE_USER, createUserAsync),
        takeEvery(types.CREATE_USER_FAILURE, failureMessage),
        takeEvery(types.CREATE_USER_SUCCESS, createUserSuccessAsync),

        takeEvery(types.UPDATE_USER, updateUserAsync),
        takeEvery(types.UPDATE_USER_FAILURE, failureMessage),
        takeEvery(types.UPDATE_USER_SUCCESS, updateUserSuccessAsync),

        takeEvery(types.UPDATE_USER_ACTIVITY_FAILURE, updateUserActivityErrorAsync),

        takeEvery(types.UPDATE_USER_CONFIRMATION_FAILURE, failureMessage),
        takeEvery(types.UPDATE_USER_CONFIRMATION_SUCCESS, createUserConfirmationSuccessAsync),

        takeEvery(types.DELETE_USER, deleteUserAsync),
        takeEvery(types.DELETE_USER_FAILURE, deleteUserFailureAsync),
        takeEvery(types.DELETE_USER_SUCCESS, deleteUserSuccessAsync),

        takeEvery(types.DELETE_USER_MEMBERSHIP, deleteUserMembershipAsync),
        takeEvery(types.DELETE_USER_MEMBERSHIP_FAILURE, failureMessage),
    ];
}



// WEBPACK FOOTER //
// ./src/store/sagas/usersSagas.js